import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Divider,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  scanAmazonByKeyword,
  saveKeywordScanToFirebase,
  BrandInfo,
  TopListing,
  KeywordSearchResult,
} from "../../services/jungleScout";
import { getSingleData } from "../../services/service";
import { RoutesUrl } from "../../common/consts/Routes";
import { CredentialsData } from "../CredentialsForm/CredentialsForm";
import CardComponent from "../shared/CardComponent";
import TableComponent, { Column } from "../shared/TableComponent";

const MARKETPLACE_USA = "us";
const MARKETPLACE_GERMANY = "de";

export default function AmazonExtract() {
  const { t } = useTranslation();
  const history = useHistory();
  const module = "modules.amazonExtract.";
  const [keyword, setKeyword] = useState("");
  const [marketplace, setMarketplace] = useState(MARKETPLACE_USA);
  const [credentials, setCredentials] = useState<CredentialsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchResult, setSearchResult] = useState<KeywordSearchResult | null>(null);

  useEffect(() => {
    getSingleData("amazonCredentials").then((data) => {
      if (data) {
        const raw = data as unknown as Record<string, string | undefined>;
        setCredentials({
          api_key_name: raw.api_key_name ?? "",
          api_key: raw.api_key ?? ""
        });
      }
    });
  }, []);

  const handleScanAmazon = async () => {
    const trimmed = keyword?.trim();
    if (!trimmed) {
      toast.warn(t(module + "keywordPlaceholder"));
      return;
    }
    const keyName = credentials?.api_key_name?.trim();
    const key = credentials?.api_key?.trim();
    if (!keyName || !key) {
      toast.warn(t(module + "apiKeyLabel") + " / " + t(module + "apiKeyNameLabel") + " required.");
      history.push(RoutesUrl.credentialsForm);
      return;
    }
    setLoading(true);
    setSearchResult(null);
    try {
      const result = await scanAmazonByKeyword(trimmed, marketplace, keyName, key);
      if (result.errors?.length) {
        toast.error(result.errors[0].detail || t(module + "error"));
        return;
      }
      setSearchResult(result);
      if (!result.searchVolume30Days && !result.brands?.length && !result.topListings?.length) {
        toast.info(t(module + "noData"));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      const is403 = msg.includes("invalid") || msg.includes("Forbidden") || msg.includes("403");
      toast.error(is403 ? t(module + "errorForbidden") : (msg || t(module + "error")));
      if (is403) {
        history.push(RoutesUrl.credentialsForm);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToInsights = async () => {
    if (!searchResult || !keyword.trim()) return;
    setSaving(true);
    try {
      await saveKeywordScanToFirebase(keyword.trim(), marketplace, searchResult);
      toast.success(t(module + "saved"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      toast.error(msg || t(module + "error"));
    } finally {
      setSaving(false);
    }
  };

  const formatNumber = (v: number | null | undefined) =>
    v != null ? String(v) : "—";
  const formatCurrency = (v: number | null | undefined) =>
    v != null && v !== 0 ? `$${Number(v).toFixed(2)}` : (v === 0 ? "$0.00" : "—");
  const formatPercentage = (v: number | null | undefined) =>
    v != null ? `${Number(v).toFixed(2)}%` : "—";

  // Brand info columns
  const brandColumns: Column[] = [
    {
      header: t(module + "brandName"),
      accessor: "brandName",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{row.brandName}</Typography>
      ),
    },
    {
      header: t(module + "totalListings"),
      accessor: "totalListings",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatNumber(row.totalListings)}</Typography>
      ),
    },
    {
      header: t(module + "averagePosition"),
      accessor: "averagePosition",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatNumber(Math.round(row.averagePosition))}</Typography>
      ),
    },
    {
      header: t(module + "averagePrice"),
      accessor: "averagePrice",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatCurrency(row.averagePrice)}</Typography>
      ),
    },
    {
      header: t(module + "organicListingsCount"),
      accessor: "organicListingsCount",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatNumber(row.organicListingsCount)}</Typography>
      ),
    },
    {
      header: t(module + "organicAveragePosition"),
      accessor: "organicAveragePosition",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatNumber(Math.round(row.organicAveragePosition))}</Typography>
      ),
    },
    {
      header: t(module + "organicAveragePrice"),
      accessor: "organicAveragePrice",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatCurrency(row.organicAveragePrice)}</Typography>
      ),
    },
    {
      header: t(module + "sponsoredListingsCount"),
      accessor: "sponsoredListingsCount",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatNumber(row.sponsoredListingsCount)}</Typography>
      ),
    },
    {
      header: t(module + "sponsoredAveragePosition"),
      accessor: "sponsoredAveragePosition",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatNumber(Math.round(row.sponsoredAveragePosition))}</Typography>
      ),
    },
    {
      header: t(module + "sponsoredAveragePrice"),
      accessor: "sponsoredAveragePrice",
      cell: (_, row: BrandInfo) => (
        <Typography variant="body2">{formatCurrency(row.sponsoredAveragePrice)}</Typography>
      ),
    },
  ];

  // Top listings columns
  const topListingsColumns: Column[] = [
    {
      header: t(module + "asin"),
      accessor: "asin",
      cell: (_, row: TopListing) => (
        <Typography variant="body2">{row.asin}</Typography>
      ),
    },
    {
      header: t(module + "title"),
      accessor: "title",
      cell: (_, row: TopListing) => (
        <Typography variant="body2">{row.title}</Typography>
      ),
    },
    {
      header: t(module + "brand"),
      accessor: "brand",
      cell: (_, row: TopListing) => (
        <Typography variant="body2">{row.brand}</Typography>
      ),
    },
    {
      header: t(module + "visits"),
      accessor: "visits",
      cell: (_, row: TopListing) => (
        <Typography variant="body2">{formatNumber(row.visits)}</Typography>
      ),
    },
    {
      header: t(module + "purchases"),
      accessor: "purchases",
      cell: (_, row: TopListing) => (
        <Typography variant="body2">{formatNumber(row.purchases)}</Typography>
      ),
    },
    {
      header: t(module + "conversionRate"),
      accessor: "conversionRate",
      cell: (_, row: TopListing) => (
        <Typography variant="body2">{formatPercentage(row.conversionRate)}</Typography>
      ),
    },
  ];

  return (
    <CardComponent title={"Amazon Scanner"} isLoading={loading}>
      <Box>
        <Grid container spacing={2} alignItems="flex-end" style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label={t(module + "keywordLabel")}
              placeholder={t(module + "keywordPlaceholder")}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="marketplace-label">
                {t(module + "marketplaceLabel")}
              </InputLabel>
              <Select
                labelId="marketplace-label"
                value={marketplace}
                onChange={(e) => setMarketplace(e.target.value as string)}
              >
                <MenuItem value={MARKETPLACE_USA}>USA</MenuItem>
                <MenuItem value={MARKETPLACE_GERMANY}>Germany</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleScanAmazon}
              disabled={loading}
              fullWidth
            >
              {loading ? t(module + "extracting") : t(module + "scanAmazon")}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSaveToInsights}
              disabled={saving || !searchResult}
              fullWidth
            >
              {saving ? t(module + "saving") : t(module + "saveToDb")}
            </Button>
          </Grid>
        </Grid>

        {searchResult && (
          <>
            {/* Search Volume and Average Ad Value */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t(module + "searchVolume30Days")}
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(searchResult.searchVolume30Days)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t(module + "averageAdValue")}
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(searchResult.averageAdValue)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Brand Information */}
            {searchResult.brands && searchResult.brands.length > 0 && (
              <Box style={{ marginBottom: 24 }}>
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                  {t(module + "brandInformation")}
                </Typography>
                <TableComponent
                  data={searchResult.brands}
                  columns={brandColumns}
                  perPage={10}
                  searchKeys={["brandName"]}
                />
              </Box>
            )}

            {/* Top Listings */}
            {searchResult.topListings && searchResult.topListings.length > 0 && (
              <Box>
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                  {t(module + "topListings")}
                </Typography>
                <TableComponent
                  data={searchResult.topListings}
                  columns={topListingsColumns}
                  perPage={10}
                  searchKeys={["asin", "title", "brand"]}
                />
              </Box>
            )}
          </>
        )}

        {!loading && !searchResult && keyword.trim() && (
          <Typography color="textSecondary" style={{ marginTop: 16 }}>
            {t(module + "noData")}
          </Typography>
        )}
      </Box>
    </CardComponent>
  );
}
